import { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2';
import { weekIdToTimestamp } from 'src/components/BeaconV2Components/SplineChartV2/splineChartV2Utils';
import { quoteWrap } from 'src/utils/stringFormatting';
import { StacklineDate } from 'stackline-datetime';
import { getNextWeekId } from 'src/utils/dateUtils';

/**
 * Formats a weekId to a human-readable week ending date string
 */
const formatWeekEnding = (weekId: number): string => {
  const timestamp = weekIdToTimestamp(weekId);
  const date = new Date(timestamp);
  const { year, week } = new StacklineDate(date).getSDSWeekYear();
  const stacklineDate = StacklineDate.getEndDateSDS({ year, week });
  return stacklineDate.toFormat('MMMM d, yyyy');
};

/**
 * Converts SplineChartV2 series data to CSV format
 * 
 * @param primarySeries - Primary series data
 * @param comparisonSeries - Comparison series data (optional)
 * @param primaryDisplayName - Display name for primary series column
 * @param comparisonDisplayName - Display name for comparison series column
 * @returns CSV string
 */
const convertSplineChartV2SeriesToDelimitedData = (
  primarySeries: SplineChartV2Series,
  comparisonSeries?: SplineChartV2Series,
  primaryDisplayName = 'Primary',
  comparisonDisplayName = 'Comparison'
): string => {
  // Build headers: Comparison first, Primary second
  const headers = [
    'WeekEnding',
    ...(comparisonSeries ? [quoteWrap(comparisonDisplayName)] : []),
    quoteWrap(primaryDisplayName)
  ];

  // Determine row count: use the longer of the two series (comparison may have more weeks)
  const rowCount = Math.max(
    primarySeries.data.length,
    comparisonSeries?.data.length ?? 0
  );

  // Get primary start weekId for computing week endings when primary data runs out
  const primaryStartWeekId = primarySeries.data[0]?.weekId ?? 0;

  // Build data rows aligned by position (index)
  // WeekEnding uses primary weekIds, computed if needed
  const dataRows = Array.from({ length: rowCount }, (_, index) => {
    // Use primary weekId if available, otherwise compute it
    const weekId = primarySeries.data[index]?.weekId ?? getNextWeekId(primaryStartWeekId, index);
    const weekEnding = quoteWrap(formatWeekEnding(weekId));
    const primaryValue = primarySeries.data[index]?.value ?? '';
    const comparisonValue = comparisonSeries?.data[index]?.value ?? '';

    return comparisonSeries
      ? [weekEnding, comparisonValue, primaryValue]
      : [weekEnding, primaryValue];
  });

  // Combine headers and data into CSV
  const csvContent = [headers, ...dataRows].map((row) => row.join(',')).join('\n');

  return `${csvContent}\n`;
};

export default convertSplineChartV2SeriesToDelimitedData;
